import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, ArrowLeft, Shield, Eye, Brain, FileText, CheckCircle } from 'lucide-react';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnrollConsent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [consents, setConsents] = useState({
    terms: false,
    privacy: false,
    dataProcessing: false,
    recording: false,
    aiAnalysis: false
  });
  
  const [signature, setSignature] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('enroll_token');
    if (!token) {
      navigate('/enroll/register');
    }
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('enroll_token')}` }
  });

  const allConsentsGiven = Object.values(consents).every(v => v) && signature.length >= 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allConsentsGiven) {
      setError('Please accept all consents and provide your signature');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/enroll/consent`,
        null,
        {
          params: {
            terms_accepted: consents.terms,
            privacy_accepted: consents.privacy,
            data_processing_accepted: consents.dataProcessing,
            recording_consent: consents.recording,
            ai_analysis_consent: consents.aiAnalysis,
            parent_signature: signature
          },
          ...getAuthHeaders()
        }
      );
      
      navigate('/enroll/child');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit consent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const consentItems = [
    {
      key: 'terms',
      icon: <FileText className="w-5 h-5" />,
      title: 'Terms of Service',
      description: 'I have read and agree to the Terms of Service governing the use of Sanjaya platform.',
      link: '/terms'
    },
    {
      key: 'privacy',
      icon: <Shield className="w-5 h-5" />,
      title: 'Privacy Policy',
      description: 'I have read and understand the Privacy Policy regarding how my and my child\'s data will be handled.',
      link: '/privacy'
    },
    {
      key: 'dataProcessing',
      icon: <Eye className="w-5 h-5" />,
      title: 'Data Processing',
      description: 'I consent to the processing of my child\'s personal data for the purpose of providing emotional support services.',
      link: null
    },
    {
      key: 'recording',
      icon: <Eye className="w-5 h-5" />,
      title: 'Session Recording',
      description: 'I consent to the recording of my child\'s sessions for quality assurance, principal review, and generating insights.',
      link: null
    },
    {
      key: 'aiAnalysis',
      icon: <Brain className="w-5 h-5" />,
      title: 'AI Analysis',
      description: 'I consent to AI-powered analysis of session data to identify patterns and generate non-diagnostic insights for my child\'s emotional development.',
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <Navigation />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-sm text-green-600">Package</span>
            </div>
            <div className="w-8 h-0.5 bg-green-500"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-sm text-green-600">Account</span>
            </div>
            <div className="w-8 h-0.5 bg-orange-500"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold">3</div>
              <span className="text-sm font-medium text-orange-600">Consent</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-300"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center text-sm">4</div>
              <span className="text-sm text-gray-400">Child</span>
            </div>
          </div>

          <Card className="border-2 border-purple-200 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-purple-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Consent & Agreements</h1>
                <p className="text-gray-600">
                  Please review and accept the following to continue
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {consentItems.map((item) => (
                  <div
                    key={item.key}
                    className={`border rounded-xl p-4 transition-all cursor-pointer ${
                      consents[item.key]
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                    onClick={() => setConsents({ ...consents, [item.key]: !consents[item.key] })}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        consents[item.key]
                          ? 'border-green-500 bg-green-500'
                          : 'border-gray-300'
                      }`}>
                        {consents[item.key] && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={consents[item.key] ? 'text-green-600' : 'text-purple-600'}>
                            {item.icon}
                          </span>
                          <h3 className="font-semibold text-gray-900">{item.title}</h3>
                          {item.link && (
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-purple-600 hover:underline"
                            >
                              (Read)
                            </a>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Digital Signature */}
                <div className="pt-4 border-t">
                  <Label className="text-base font-semibold">Digital Signature *</Label>
                  <p className="text-sm text-gray-500 mb-2">
                    Type your full name as your digital signature
                  </p>
                  <Input
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    placeholder="Type your full name"
                    className="font-serif text-lg italic"
                    required
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                    {error}
                  </div>
                )}

                <div className="pt-4 flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/enroll/register')}
                    className="px-6"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  
                  <Button
                    type="submit"
                    disabled={!allConsentsGiven || loading}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white py-6 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Continue to Child Profile'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EnrollConsent;
